import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import '../../../assets/scss/components/qr/AttendanceInfo.scss';

const AttendanceInfo = ({ onComplete, onBack }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const infoSlides = [
    {
      id: 'risks',
      title: 'Riesgos e impactos HSEQ-SV',
      backgroundSvg: 'safety-bg',
      items: [
        { icon: '🏗️', text: 'Riesgo Biomecánico: Dolor lumbar, túnel carpiano, fatiga visual por uso prolongado del computador.' },
        { icon: '💧', text: 'Riesgo Locativo: Caídas al mismo nivel por pisos húmedos u objetos mal ubicados.' },
        { icon: '⚡', text: 'Riesgo Eléctrico: Descargas o incendios por sobrecarga o instalaciones defectuosas.' },
        { icon: '🧠', text: 'Riesgo Psicosocial: Estrés laboral, sobrecarga de tareas, mal clima laboral.' },
        { icon: '🌿', text: 'Riesgo Ambiental: Mala disposición de residuos, contaminación visual o de olores.' },
        { icon: '🚪', text: 'Riesgo Público / Seguridad: Ingreso de personas no autorizadas.' },
        { icon: '⚙️', text: 'Riesgo Mecánico / Físico: Golpes con puertas, cajones, archivadores.' },
        { icon: '🚗', text: 'Riesgo Vial – Interno: Movimiento de vehículos institucionales (mensajería o transportes).' },
        { icon: '🚙', text: 'Riesgo Vial – Externo: Accidente en misión o desplazamiento laboral.' },
        { icon: '☢️', text: 'Riesgo Químico – Limpieza: Exposición a productos irritantes o tóxicos mal almacenados.' }
      ]
    },
    {
      id: 'controls',
      title: 'Controles de HSEQ-SV',
      backgroundSvg: 'controls-bg',
      items: [
        { icon: '💺', text: 'Sillas ergonómicas, pausas activas, buena iluminación, capacitaciones en higiene postural.' },
        { icon: '🧹', text: 'Señalización, orden y limpieza, uso de tapetes antideslizantes.' },
        { icon: '🔌', text: 'Tomas certificadas, mantenimiento preventivo, inspecciones periódicas.' },
        { icon: '🌱', text: 'Programas de bienestar, pausas activas, escucha activa, comunicación asertiva.' },
        { icon: '♻️', text: 'Clasificación de residuos, puntos ecológicos, señalización ambiental.' },
        { icon: '🎥', text: 'Control de ingreso, identificación visible, cámaras de seguridad.' },
        { icon: '🏢', text: 'Mobiliario en buen estado, cultura de orden, rutas despejadas.' },
        { icon: '🚦', text: 'Revisión preoperacional, uso de EPP vial, señalización interna.' },
        { icon: '🚗', text: 'Capacitación en conducción segura, rutas seguras, no uso del celular.' },
        { icon: '🧤', text: 'Etiquetado adecuado, uso de guantes y tapabocas, capacitación.' }
      ]
    }
  ];

  const currentInfo = infoSlides[currentSlide];
  const isLastSlide = currentSlide === infoSlides.length - 1;

  const handleNext = () => {
    if (isLastSlide) {
      onComplete();
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide === 0) {
      onBack();
    } else {
      setCurrentSlide(prev => prev - 1);
    }
  };

  return (
    <div className="attendance-info">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentInfo.id}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          className="info-slide"
        >
          <div className={`slide-background ${currentInfo.backgroundSvg}`}>
            {/* SVG de fondo dinámico */}
            <svg viewBox="0 0 600 400" className="bg-svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e0f2f1" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" opacity="0.3" />
              
              {currentSlide === 0 && (
                <>
                  <circle cx="500" cy="100" r="80" fill="#4caf50" opacity="0.2" />
                  <path d="M 50 350 Q 150 250 250 350" stroke="#4caf50" strokeWidth="3" fill="none" opacity="0.3" />
                </>
              )}
              
              {currentSlide === 1 && (
                <>
                  <rect x="450" y="50" width="100" height="100" rx="10" fill="#2196f3" opacity="0.2" />
                  <polygon points="100,50 150,150 50,150" fill="#2196f3" opacity="0.2" />
                </>
              )}
            </svg>
          </div>

          <div className="slide-content">
            <h2 className="slide-title">{currentInfo.title}</h2>
            
            <div className="info-grid">
              {currentInfo.items.map((item, index) => (
                <motion.div
                  key={index}
                  className="info-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="item-icon">{item.icon}</span>
                  <p className="item-text">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="slide-footer">
            <div className="slide-dots">
              {infoSlides.map((_, index) => (
                <span
                  key={index}
                  className={`dot ${index === currentSlide ? 'active' : ''}`}
                />
              ))}
            </div>

            <div className="slide-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handlePrev}
              >
                <PrevIcon />
                {currentSlide === 0 ? 'Atrás' : 'Anterior'}
              </button>
              
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
              >
                {isLastSlide ? (
                  <>
                    Continuar
                    <CheckIcon />
                  </>
                ) : (
                  <>
                    Siguiente
                    <NextIcon />
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AttendanceInfo;